'use client';

import { useRef, useCallback } from 'react';

/**
 * Storage types that can be requested via the Storage Access API.
 * Safari 16.2+ and Chrome 125+ support requesting specific storage types.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/requestStorageAccess
 */
const STORAGE_ACCESS_OPTIONS = {
  // All storage types used by the app
  all: true,
} as const;

/**
 * Requests unpartitioned storage access for third-party iframe contexts.
 * This is required for iOS Safari ITP and Chrome's storage partitioning.
 *
 * The function tries modern API first (with options), then falls back to basic API.
 * Both grant access to cookies, localStorage, sessionStorage, and IndexedDB.
 */
export async function requestStorageAccess(): Promise<boolean> {
  if (typeof document === 'undefined' || !('requestStorageAccess' in document)) {
    return false;
  }

  try {
    // Modern API (Safari 16.2+, Chrome 125+) - request all storage types
    // When called with options, returns a handle; without options, returns void
    await (
      document.requestStorageAccess as (options?: typeof STORAGE_ACCESS_OPTIONS) => Promise<unknown>
    )(STORAGE_ACCESS_OPTIONS);
    return true;
  } catch {
    // Fallback to basic API for older browsers (Firefox, older Safari/Chrome)
    // Basic API grants access to cookies and unpartitioned storage
    try {
      await document.requestStorageAccess();
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Hook that requests storage access on first user interaction.
 * Must be called from a user gesture (click, tap, keypress) to succeed.
 *
 * Note: This is different from `useStorageAccess` in storage-access-provider.tsx
 * which checks if storage is available. This hook *requests* access.
 */
export function useRequestStorageAccess() {
  const hasRequestedRef = useRef(false);

  const requestOnInteraction = useCallback(() => {
    if (hasRequestedRef.current) {
      return;
    }
    hasRequestedRef.current = true;
    requestStorageAccess().catch(() => {});
  }, []);

  return { requestOnInteraction };
}

/**
 * @deprecated Use `useRequestStorageAccess` instead. This alias exists for backward compatibility.
 */
export const useStorageAccess = useRequestStorageAccess;
